// Corta o texto de OUTRAS seções da fatura que vaza para dentro do endereço do cliente.
//
// O endereço sai do fatura-parser como um join(', ') das linhas entre o "CNPJ/CPF:" e o
// "CEP:" seguinte (ver fatura-parser/secoes.ts). Quando o PDF quebra o bloco do cliente
// em duas colunas, o texto do MEDIDOR cai no meio e o endereço volta com meia fatura
// dentro — e com o endereço de verdade REPETIDO no fim:
//
//   "RUA 30, Q. G15, ..., ED - AME INFINITY, HOME, Tipo de fornecimento: TRIFÁSICO,
//    Leitura Anterior, Leitura Atual, Data das, Leituras, 13/07/2026, 12/08/2026,
//    N° de Dias, 30, RUA 30, Q. G15, ..., ED - AME INFINITY HOME"   (269 caracteres)
//
// O Asaas corta `address` em 255 e recusa a criação do cliente com 400 ("O campo address
// deve ter no máximo 255 caracteres"). Como o cadastro do pagador só é criado na PRIMEIRA
// emissão da UC (depois vem do cache `unidades_consumidoras.asaas_customer_id`), o efeito
// era uma UC que simplesmente não emitia — foi o que travou o boleto da Gran da fatura
// 10239 em 21/08/2026.
//
// Os marcadores vivem aqui, e não em cada ponta, porque são usados nos dois lados e não
// podem divergir: o parser corta na origem (fatura nova) e a asaas-cobranca corta na
// borda (as ~40 faturas que já foram gravadas sujas, e qualquer variação futura).

/** Limite dos campos `address` e `province` no Asaas (POST /v3/customers). */
export const LIMITE_ENDERECO_ASAAS = 255;

/**
 * Início de outra seção da fatura. O endereço do cliente ACABA aqui: tudo daí em diante
 * é texto de outra coluna, inclusive a repetição do próprio endereço.
 */
const MARCADORES_FIM_ENDERECO: RegExp[] = [
  /tipo\s+de\s+fornecimento/i,
  /leitura\s+(?:anterior|atual)/i,
  /^data\s+das(?:\s+leituras?)?$/i,
  /n[.ºo°]{0,2}\s*de\s+dias/i,
  /previs[ãa]o\s+pr[óo]xima\s+leitura/i,
  /constante\s+de\s+medi/i,
  /classifica[çc][ãa]o\s*:/i,
  // A linha de CEP fecha o bloco de endereço. O OCR troca C/E/P latinos por
  // cirílicos (СЕР) em parte das faturas — mesmo homóglifo que já quebrava o regex do
  // CEP no parser —, então o marcador aceita os dois alfabetos. Caso real: fatura 8428.
  /[CС][EЕ][PР]\s*:/,
];

/** Carimbos que a Equatorial imprime ACIMA do endereço; não são seção nova, só prefixo. */
const PREFIXOS_DESCARTAVEIS: RegExp[] = [
  /^segunda\s+via$/i,
  /^2[ªa]\s*via$/i,
];

/** A linha marca o fim do bloco de endereço (tudo a partir dela é de outra seção). */
export function ehFimDoEndereco(linha: string): boolean {
  const t = linha.trim();
  return MARCADORES_FIM_ENDERECO.some((re) => re.test(t));
}

/** Corta o array de linhas do bloco de endereço no primeiro marcador de outra seção. */
export function cortaLinhasDoEndereco(linhas: string[]): string[] {
  const fim = linhas.findIndex(ehFimDoEndereco);
  const uteis = fim === -1 ? linhas : linhas.slice(0, fim);
  let inicio = 0;
  while (inicio < uteis.length && PREFIXOS_DESCARTAVEIS.some((re) => re.test(uteis[inicio].trim()))) inicio++;
  return uteis.slice(inicio);
}

/**
 * Saneia um endereço JÁ concatenado (o que está gravado em `faturas.end_logradouro` e
 * `unidades_consumidoras.logradouro`). Opera por segmento de vírgula porque é assim que o
 * parser junta as linhas. Nunca lança; string vazia entra e sai vazia.
 *
 * O truncamento em `limite` é a última linha de defesa: mesmo um ruído que não casa com
 * nenhum marcador não pode mais derrubar a emissão.
 */
export function limpaEnderecoFatura(raw?: string | null, limite = LIMITE_ENDERECO_ASAAS): string {
  const texto = (raw || '').replace(/\s+/g, ' ').trim();
  if (!texto) return '';

  const segmentos = cortaLinhasDoEndereco(texto.split(',').map((s) => s.trim()).filter(Boolean));
  const limpo = segmentos.join(', ').replace(/,\s*,/g, ',').replace(/^[\s,;-]+|[\s,;-]+$/g, '');

  if (limpo.length <= limite) return limpo;
  // Corta na última vírgula que couber, para não partir um segmento no meio.
  const cortado = limpo.slice(0, limite);
  const ultimaVirgula = cortado.lastIndexOf(',');
  return (ultimaVirgula > 0 ? cortado.slice(0, ultimaVirgula) : cortado).replace(/[\s,;-]+$/g, '');
}
